<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage, InfoTooltip, Switch, Tooltip } from '@/shared/ui';
  import { formatInstantToPtBR } from '@/shared/lib';
  import HistoryIcon from '@/shared/assets/icons/HistoryIcon.svg';
  import SettingsIcon from '@/shared/assets/icons/SettingsIcon.svg';
  import PencilIcon from '@/shared/assets/icons/PencilBlack.svg';
  import InfoIcon from '@/shared/assets/icons/InfoBoldIcon.svg';
  import EllipsisIcon from '@/shared/assets/icons/EllipsisIcon.svg';
  import ChevronIcon from '@/shared/assets/icons/ArrowDown.svg';
  import RecordListRowMenu from './RecordListRowMenu.vue';
  import type { RecordListRow } from '../model/records.types';

  /**
   * Actions column of the record list. Kept apart from `RecordActionsCell`, which
   * serves Modelos, Rascunhos and Reprovados with a contract of four actions:
   * turning that one into the eight controls of this tab would change three
   * screens nobody asked to change.
   */
  const props = withDefaults(
    defineProps<{
      record: RecordListRow;
      isRevisionRow?: boolean;
      expanded?: boolean;
      menuOpen?: boolean;
      /**
       * Only a SUPERUSER inactivates a record, so for everyone else the switch is
       * not rendered at all. The page owns the decision, the same way the
       * "Superadmin" switch of Criar/Editar Usuário is decided by its page.
       */
      showInactivateToggle?: boolean;
      /**
       * Whether this row has previous revisions. It comes from the listing, so the
       * arrow appears without the screen fetching the revisions of every row.
       */
      hasOlderRevisions?: boolean;
      /**
       * A status write of this row is in flight. The switch has already moved, because the change
       * is optimistic, so it is disabled until the write settles: a second click would queue
       * another request whose answer could land before the first one and leave the row on the
       * older status.
       */
      togglePending?: boolean;
    }>(),
    {
      isRevisionRow: false,
      expanded: false,
      menuOpen: false,
      showInactivateToggle: false,
      hasOlderRevisions: false,
      togglePending: false,
    }
  );

  const emit = defineEmits<{
    'toggle-expand': [];
    'update:menu-open': [open: boolean];
    'open-history': [];
    'open-control': [];
    edit: [];
    configure: [];
    'edit-homologation': [];
    delete: [];
    'toggle-active': [isActive: boolean];
  }>();

  /**
   * `info` opens the audit card instead of a text tooltip, and the order of the
   * whole list is the order of the design.
   */
  const ICON_ACTIONS = [
    { key: 'history', label: 'Histórico', icon: HistoryIcon },
    { key: 'control', label: 'Controle', icon: SettingsIcon },
    { key: 'edit', label: 'Editar', icon: PencilIcon },
    { key: 'info', label: 'Informações', icon: InfoIcon },
    { key: 'more', label: 'Mais ações', icon: EllipsisIcon },
  ];

  // Tippy binds `focus` to the reference itself, and focus does not bubble, so a
  // tooltip wrapping a button only answers the keyboard through `focusin`.
  const TOOLTIP_TRIGGER = 'mouseenter focusin';

  const toggleLabel = computed(() => (props.record.isActive ? 'Inativar' : 'Ativar'));

  const showChevron = computed(() => !props.isRevisionRow && props.hasOlderRevisions);

  const chevronLabel = computed(() =>
    props.expanded ? 'Recolher revisões anteriores' : 'Expandir revisões anteriores'
  );

  const handleIconAction = (key: string) => {
    if (key === 'history') emit('open-history');
    if (key === 'control') emit('open-control');
    if (key === 'edit') emit('edit');
  };

  const audit = computed(() => ({
    createdAt: formatInstantToPtBR(props.record.audit.createdAt),
    createdBy: props.record.audit.createdBy?.name,
    updatedAt: formatInstantToPtBR(props.record.audit.updatedAt),
    updatedBy: props.record.audit.updatedBy?.name,
  }));
</script>

<template>
  <!--
    The chevron is 36 tall and the rest of the controls are 24: the vertical
    padding here is smaller than in the other cells so that the row keeps the 60
    of the design, and the reserved height keeps every record row at the same
    height whether it has a chevron or not.
  -->
  <td class="px-6 py-3 text-sm text-gray-07 text-left align-middle">
    <div
      class="flex items-center gap-4"
      :class="{ 'min-h-9': !props.isRevisionRow }"
    >
      <!-- The icon set is in the subtext grey of the design; the chevron keeps the
           darker text colour of the cell. -->
      <div class="flex items-center gap-5 text-gray-xx-02">
        <div class="flex items-center gap-1">
          <template
            v-for="action in ICON_ACTIONS"
            :key="action.key"
          >
            <InfoTooltip
              v-if="action.key === 'info'"
              :icon="action.icon"
              placement="left"
              :trigger="TOOLTIP_TRIGGER"
              :created-at="audit.createdAt"
              :created-by="audit.createdBy"
              :updated-at="audit.updatedAt"
              :updated-by="audit.updatedBy"
            >
              <button
                type="button"
                :aria-label="action.label"
                :data-testid="`record-action-${action.key}`"
                class="flex items-center"
              >
                <IconImage
                  :src="action.icon"
                  alt=""
                />
              </button>
            </InfoTooltip>
            <RecordListRowMenu
              v-else-if="action.key === 'more'"
              :icon="action.icon"
              :label="action.label"
              :open="props.menuOpen"
              @update:open="emit('update:menu-open', $event)"
              @configure="emit('configure')"
              @edit-homologation="emit('edit-homologation')"
              @delete="emit('delete')"
            />
            <Tooltip
              v-else
              :content="action.label"
              :trigger="TOOLTIP_TRIGGER"
              placement="top"
              class="flex items-center"
            >
              <button
                type="button"
                :aria-label="action.label"
                :data-testid="`record-action-${action.key}`"
                class="flex items-center"
                @click="handleIconAction(action.key)"
              >
                <IconImage
                  :src="action.icon"
                  alt=""
                />
              </button>
            </Tooltip>
          </template>
        </div>
        <Tooltip
          v-if="!props.isRevisionRow && props.showInactivateToggle"
          :content="toggleLabel"
          :trigger="TOOLTIP_TRIGGER"
          placement="top"
          class="flex items-center"
        >
          <Switch
            size="lg"
            :model-value="props.record.isActive"
            :disabled="props.togglePending"
            :aria-label="toggleLabel"
            :data-testid="`record-action-toggle-${props.record.id}`"
            @update:model-value="emit('toggle-active', $event)"
          />
        </Tooltip>
      </div>
      <button
        v-if="showChevron"
        type="button"
        class="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-02 bg-white-01 shadow-xs"
        :aria-expanded="props.expanded"
        :aria-label="chevronLabel"
        :data-testid="`record-revisions-expand-${props.record.id}`"
        @click="emit('toggle-expand')"
      >
        <!--
          The svg loader strips the viewBox of every icon, so a CSS size does not
          scale the drawing and pushes it off centre, which the rotation turns
          into a visible offset. The icon is used at its own 20 of the design and
          it already points down, so only the expanded state rotates.
        -->
        <IconImage
          :src="ChevronIcon"
          alt=""
          class="transition-transform duration-200"
          :class="{ 'rotate-180': props.expanded }"
        />
      </button>
    </div>
  </td>
</template>
